import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import RequireClientAdmin from "../../components/client/RequireClientAdmin";
import SyncStatusBanner from "../../components/client/SyncStatusBanner";
import { clientApi } from "../../api";

/**
 * Data Quality — finds members with missing core fields (email, mobile,
 * postcode, DOB, gender, occupation, analysis tags). Useful for venue
 * ops to run cleanup drives before a marketing campaign.
 */
export default function DataQualityReport() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drillGap, setDrillGap] = useState(null);
  const [drillRows, setDrillRows] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    clientApi.getDataQuality()
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function openDrill(gap) {
    setDrillGap(gap); setDrillRows(null); setDrillLoading(true);
    try {
      const d = await clientApi.getDataQualityMembers(gap.key);
      setDrillRows(d.rows || []);
    } catch (err) {
      setError(err.message);
      setDrillRows([]);
    }
    setDrillLoading(false);
  }

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>Data Quality</h1>
          <p className="page-subtitle">Active members with missing contact or profile fields</p>
        </div>
      </div>

      <SyncStatusBanner cache={data?.cache} />

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {loading && <div style={{ color: "var(--text-muted)" }}>Loading…</div>}

      {data && (
        <>
          <div className="stat-card" style={{ marginBottom: "1rem" }}>
            <span className="stat-label">Active members analysed</span>
            <span className="stat-value">{data.total}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0.75rem" }}>
            {data.gaps.map((g) => (
              <button
                key={g.key}
                onClick={() => openDrill(g)}
                className="settings-card"
                style={{ textAlign: "left", cursor: "pointer", border: "1px solid var(--border)" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontWeight: 600 }}>{g.label}</span>
                  <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{g.pct}%</span>
                </div>
                <div style={{ fontSize: "1.6rem", fontWeight: 700, margin: "0.25rem 0" }}>{g.count.toLocaleString()}</div>
                <div style={{ height: 4, borderRadius: 999, background: "var(--bg-elevated)" }}>
                  <div style={{
                    height: "100%", width: `${g.pct}%`,
                    background: g.pct >= 50 ? "var(--danger)" : g.pct >= 20 ? "var(--warning)" : "var(--success)",
                    borderRadius: 999,
                  }} />
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {drillGap && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDrillGap(null)}>
          <div className="modal" style={{ maxWidth: 760 }}>
            <div className="modal-header">
              <div className="modal-header-text">
                <h2>{drillGap.label}</h2>
                <p>Sample of up to 200 affected active members.</p>
              </div>
              <button className="modal-close-btn" onClick={() => setDrillGap(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal-body">
              {drillLoading ? <div style={{ color: "var(--text-muted)" }}>Loading…</div> : (
                drillRows?.length ? (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Member #</th><th>Name</th><th>Email</th><th>Mobile</th></tr></thead>
                      <tbody>
                        {drillRows.map((r) => (
                          <tr key={r.membernumber}>
                            <td><Link to={`/client/members/${r.membernumber}`} onClick={() => setDrillGap(null)}><code>#{r.membernumber}</code></Link></td>
                            <td>{r.firstname} {r.surname}</td>
                            <td style={{ fontSize: "0.85rem", color: r.email ? "var(--text)" : "var(--text-muted)" }}>{r.email || "—"}</td>
                            <td style={{ fontSize: "0.85rem", color: r.mobile ? "var(--text)" : "var(--text-muted)" }}>{r.mobile || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="empty-state"><p>No members with this gap.</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
